import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  ButtonGroup,
} from 'shards-react';

import {
  FiAlertCircle,
  FiArrowLeft,
  FiRefreshCcw,
  FiZoomIn,
} from 'react-icons/fi';

import PageTitle from '../../components/common/PageTitle';
import LoadingContainer from '../../components/layout/LodingContainer';

import { getRenotarizationHistoryRequest } from '../../store/modules/renotarizationLogs/actions';
import { getAllInstitutionsRequest } from '../../store/modules/institution/actions';

import { dateAndSeconds } from '../../utils/dateFormatter';
import { showErrorLogModal } from '../../utils/errorLogModal';

const RESTAMP_STATUS = {
  success: 'Sucesso',
  error: 'Erro',
};

export default function RenotarizationLogDetails() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const doc = location.state?.doc;

  const institutions = useSelector((state) => state.institution.institutions);
  const { history, historyLoading } = useSelector(
    (state) => state.renotarizationLogs
  );

  const institutionByClientId = institutions
    ? Object.fromEntries(institutions.map((i) => [i.client_id, i]))
    : {};

  const institution = doc ? institutionByClientId[doc.client_id] : null;
  const institutionLabel = institution
    ? `${institution.name} - ${institution.initials}`
    : doc?.client_id;

  function fetchHistory() {
    if (doc) {
      dispatch(getRenotarizationHistoryRequest(doc.your_number, doc.client_id));
    }
  }

  /*eslint-disable */
  useEffect(() => {
    dispatch(getAllInstitutionsRequest());
    fetchHistory();
  }, []);
  /* eslint-enable */

  function handleRefresh() {
    fetchHistory();
  }

  function handleBack() {
    navigate(-1);
  }

  function handleShowLogs(errorLog) {
    showErrorLogModal(errorLog);
  }

  const historyList = Array.isArray(history) ? history : [];

  return (
    <Container fluid className="main-content-container px-4">
      <Row noGutters className="page-header py-4">
        <PageTitle
          sm="4"
          title="Registro de Renotarizações"
          subtitle="Logs"
          className="text-sm-left"
        />
      </Row>

      <Row className="mb-3">
        <Col md="6">
          <button
            type="button"
            onClick={handleBack}
            className="text-decoration-none h5 text-primary"
            style={{
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
            }}
          >
            <FiArrowLeft /> <span className="ml-1">Voltar</span>
          </button>
        </Col>
        <Col md="6" className="text-right">
          <Button disabled={historyLoading} onClick={handleRefresh}>
            <span className="text-white">
              <FiRefreshCcw />
            </span>{' '}
            Atualizar
          </Button>
        </Col>
      </Row>

      {/* Detalhes do documento */}
      <Row>
        <Col>
          <Card small className="mb-4">
            <CardHeader className="border-bottom">
              <h6 className="mt-1 mr-3 mb-0">Detalhes do documento</h6>
            </CardHeader>
            <CardBody className="p-0 overflow-auto">
              {!doc ? (
                <div className="col-sm-12 d-flex justify-content-center">
                  <h5 className="mt-3">
                    <FiAlertCircle className="mr-2" />
                    Informações do documento não disponíveis.
                  </h5>
                </div>
              ) : (
                <table className="table m-0">
                  <tbody>
                    <tr>
                      <td>Instituição</td>
                      <td>{institutionLabel}</td>
                    </tr>
                    <tr>
                      <td>Seu número</td>
                      <td>{doc.your_number}</td>
                    </tr>
                    <tr>
                      <td>Tipo de documento</td>
                      <td>{doc.document_type || 'não disponível'}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Histórico de processamento */}
      <Row>
        <Col>
          <Card small className="mb-4">
            <CardHeader className="border-bottom">
              <h6 className="mt-1 mr-3 mb-0">Histórico de processamento</h6>
            </CardHeader>
            <CardBody className="p-0 overflow-auto">
              {historyLoading && <LoadingContainer loading={historyLoading} />}
              {!historyLoading && history !== null && (
                <>
                  {historyList.length === 0 ? (
                    <div className="col-sm-12 d-flex justify-content-center">
                      <h5 className="mt-3">
                        <FiAlertCircle className="mr-2" />
                        Não existem registros de processamento para este
                        documento.
                      </h5>
                    </div>
                  ) : (
                    <table className="table m-0">
                      <thead className="bg-light">
                        <tr>
                          <th scope="col" className="border-0">
                            Id
                          </th>
                          <th scope="col" className="border-0">
                            Id no Conector
                          </th>
                          <th scope="col" className="border-0">
                            Qtd. de recarimbamentos
                          </th>
                          <th scope="col" className="border-0">
                            Método de recarimbamento
                          </th>
                          <th scope="col" className="border-0">
                            Expiração anterior do carimbo
                          </th>
                          <th scope="col" className="border-0">
                            Expiração atual do carimbo
                          </th>
                          <th scope="col" className="border-0">
                            Status
                          </th>
                          <th scope="col" className="border-0">
                            Data/Hora
                          </th>
                          <th scope="col" className="border-0">
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyList.map((item, index) => (
                          <tr key={item._id || index}>
                            <td>{item._id}</td>
                            <td>{item.document_id || '-'}</td>
                            <td>{item.document_restamps_count ?? '-'}</td>
                            <td>{item.document_restamp_method || '-'}</td>
                            <td>
                              {item.previous_timestamp_expiration
                                ? dateAndSeconds(
                                    item.previous_timestamp_expiration
                                  )
                                : '-'}
                            </td>
                            <td>
                              {item.current_timestamp_expiration
                                ? dateAndSeconds(
                                    item.current_timestamp_expiration
                                  )
                                : '-'}
                            </td>
                            <td>
                              {RESTAMP_STATUS[item.restamp_result] ||
                                item.restamp_result ||
                                '-'}
                            </td>
                            <td>
                              {item.date_time
                                ? dateAndSeconds(item.date_time)
                                : '-'}
                            </td>
                            <td className="align-middle">
                              <ButtonGroup size="sm">
                                <Button
                                  theme="white"
                                  disabled={item.restamp_result !== 'error'}
                                  onClick={() =>
                                    handleShowLogs(item.restamp_error_message)
                                  }
                                >
                                  <span className="text-light">
                                    <FiZoomIn />
                                  </span>{' '}
                                  Logs
                                </Button>
                              </ButtonGroup>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
